import React, { useEffect, useState } from 'react'
import {
  backendService,
  type FarmPricing,
  type FarmProfile,
  type MarketplaceBid,
  type MarketplaceBidCreateRequest,
  type MarketplaceJob,
  type MarketplaceNotification,
  type MarketplaceQuote,
  type MarketplaceQuoteCreateRequest,
  type MarketplaceWorkflowStatus
} from '../../services/backendService'
import { AvailableJobsList } from './components/AvailableJobsList'
import { JobDetailsForFarm } from './components/JobDetailsForFarm'
import { BidSubmissionForm } from './components/BidSubmissionForm'
import { QuoteAcceptForm } from './components/QuoteAcceptForm'
import { FarmBidsList } from './components/FarmBidsList'
import { FarmActiveJobsList } from './components/FarmActiveJobsList'
import { MarketplaceInbox } from './components/MarketplaceInbox'
import { FarmAIChat } from './components/FarmAIChat'
import { PresetsBrowser } from '../LibraryManager/PresetsBrowser'
import './FarmMarketplace.css'

interface FarmMarketplaceProps {
  activeTab: 'browse' | 'my-jobs' | 'active-jobs' | 'my-bids' | 'inbox' | 'dashboard' | 'settings' | 'girasoles-assistant' | 'presets'
  onUnreadCountChange?: (count: number) => void
  onOpenJob?: (jobId: string) => void
  pendingJobId?: string | null
  onPendingJobConsumed?: () => void
}

interface FarmProfileFormState {
  phone: string
  website: string
  description: string
  logoUrl: string
  addressLine1: string
  addressLine2: string
  city: string
  stateProvince: string
  postalCode: string
  country: string
  serviceRadiusKm: string
  averageTurnaroundDays: string
  shipsNationally: boolean
  shipsInternationally: boolean
  printerCount: string
  printerModels: string
  materialsAvailable: string
  maxBuildVolumeX: string
  maxBuildVolumeY: string
  maxBuildVolumeZ: string
  layerHeights: string
  certifications: string
  medicalGradeCapable: boolean
  biocompatibleMaterials: boolean
  cleanRoomAvailable: boolean
  sterilizationCapable: boolean
}

interface DaySchedule {
  enabled: boolean
  open: string
  close: string
  delivers: boolean
  deliverUntil: string
}

type WeeklySchedule = Record<string, DaySchedule>

interface FarmAvailabilityFormState {
  schedule: WeeklySchedule
  vacationMode: boolean
  vacationStart: string
  vacationEnd: string
  vacationMessage: string
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
}

interface FarmPricingFormState {
  minimumOrderValue: string
  rushOrderAvailable: boolean
  rushOrderMultiplier: string
  bulkDiscountAvailable: boolean
  acceptsCustomMaterials: boolean
}

const defaultSchedule: WeeklySchedule = {
  monday:    { enabled: true,  open: '08:00', close: '17:00', delivers: true,  deliverUntil: '16:00' },
  tuesday:   { enabled: true,  open: '08:00', close: '17:00', delivers: false, deliverUntil: '' },
  wednesday: { enabled: true,  open: '08:00', close: '17:00', delivers: false, deliverUntil: '' },
  thursday:  { enabled: true,  open: '08:00', close: '17:00', delivers: false, deliverUntil: '' },
  friday:    { enabled: true,  open: '08:00', close: '17:00', delivers: true,  deliverUntil: '16:00' },
  saturday:  { enabled: false, open: '09:00', close: '13:00', delivers: false, deliverUntil: '' },
  sunday:    { enabled: false, open: '09:00', close: '13:00', delivers: false, deliverUntil: '' },
}

const parseSchedule = (raw: unknown): WeeklySchedule => {
  const schedule: WeeklySchedule = { ...defaultSchedule }
  if (!raw || typeof raw !== 'object') return schedule
  const hours = raw as Record<string, unknown>
  for (const day of DAYS) {
    const val = hours[day]
    if (!val) continue
    if (typeof val === 'string') {
      // backward compat: "08:00-17:00"
      const [open, close] = val.split('-')
      schedule[day] = { enabled: true, open: open ?? '08:00', close: close ?? '17:00', delivers: false, deliverUntil: '' }
    } else if (typeof val === 'object') {
      const d = val as Partial<DaySchedule> & { closed?: boolean }
      // Backend stores "closed"; frontend uses "enabled" (inverted)
      const closed = d.closed !== undefined ? d.closed : (d.enabled !== undefined ? !d.enabled : false)
      schedule[day] = {
        enabled:      !closed,
        open:         d.open        ?? '08:00',
        close:        d.close       ?? '17:00',
        delivers:     d.delivers    ?? false,
        deliverUntil: d.deliverUntil ?? '',
      }
    }
  }
  return schedule
}

const emptyProfileForm: FarmProfileFormState = {
  phone: '',
  website: '',
  description: '',
  logoUrl: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  country: '',
  serviceRadiusKm: '',
  averageTurnaroundDays: '',
  shipsNationally: false,
  shipsInternationally: false,
  printerCount: '',
  printerModels: '',
  materialsAvailable: '',
  maxBuildVolumeX: '',
  maxBuildVolumeY: '',
  maxBuildVolumeZ: '',
  layerHeights: '',
  certifications: '',
  medicalGradeCapable: false,
  biocompatibleMaterials: false,
  cleanRoomAvailable: false,
  sterilizationCapable: false
}

const emptyAvailabilityForm: FarmAvailabilityFormState = {
  schedule: { ...defaultSchedule },
  vacationMode: false,
  vacationStart: '',
  vacationEnd: '',
  vacationMessage: ''
}

const emptyPricingForm: FarmPricingFormState = {
  minimumOrderValue: '',
  rushOrderAvailable: false,
  rushOrderMultiplier: '',
  bulkDiscountAvailable: false,
  acceptsCustomMaterials: false
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

const formatDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '')

const formatVerificationStatus = (value?: string | null) => {
  if (!value) {
    return 'Pending'
  }

  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const formatFileSize = (size: number) => {
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
  }

  if (size >= 1024) {
    return `${(size / 1024).toFixed(1)} KB`
  }

  return `${size} B`
}

const joinList = (values?: Array<string | number> | null) => (values ?? []).join(', ')

const parseStringList = (value: string) => value
  .split(',')
  .map(item => item.trim())
  .filter(Boolean)

const parseOptionalNumber = (value: string, fieldName: string) => {
  const trimmed = value.trim()

  if (!trimmed) {
    return undefined
  }

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) {
    throw new Error(`${fieldName} must be a valid number`)
  }

  return parsed
}

const buildProfileFormState = (profile: FarmProfile): FarmProfileFormState => ({
  phone: profile.phone ?? '',
  website: profile.website ?? '',
  description: profile.description ?? '',
  logoUrl: profile.logoUrl ?? '',
  addressLine1: profile.address.line1 ?? '',
  addressLine2: profile.address.line2 ?? '',
  city: profile.address.city ?? '',
  stateProvince: profile.address.stateProvince ?? '',
  postalCode: profile.address.postalCode ?? '',
  country: profile.address.country ?? '',
  serviceRadiusKm: profile.serviceRadiusKm?.toString() ?? '',
  averageTurnaroundDays: profile.averageTurnaroundDays?.toString() ?? '',
  shipsNationally: profile.shipsNationally,
  shipsInternationally: profile.shipsInternationally,
  printerCount: profile.capabilities.printerCount?.toString() ?? '',
  printerModels: joinList(profile.capabilities.printerModels),
  materialsAvailable: joinList(profile.capabilities.materialsAvailable),
  maxBuildVolumeX: profile.capabilities.maxBuildVolume?.x?.toString() ?? '',
  maxBuildVolumeY: profile.capabilities.maxBuildVolume?.y?.toString() ?? '',
  maxBuildVolumeZ: profile.capabilities.maxBuildVolume?.z?.toString() ?? '',
  layerHeights: joinList(profile.capabilities.layerHeights),
  certifications: joinList(profile.capabilities.certifications),
  medicalGradeCapable: profile.capabilities.medicalGradeCapable,
  biocompatibleMaterials: profile.capabilities.biocompatibleMaterials,
  cleanRoomAvailable: profile.capabilities.cleanRoomAvailable,
  sterilizationCapable: profile.capabilities.sterilizationCapable
})

const buildAvailabilityFormState = (profile: FarmProfile): FarmAvailabilityFormState => ({
  schedule: parseSchedule(profile.availability.operatingHours),
  vacationMode: profile.availability.vacationMode,
  vacationStart: formatDateInput(profile.availability.vacationStart),
  vacationEnd: formatDateInput(profile.availability.vacationEnd),
  vacationMessage: profile.availability.vacationMessage ?? ''
})

const buildPricingFormState = (pricing: FarmPricing): FarmPricingFormState => ({
  minimumOrderValue: pricing.minimumOrderValue.toString(),
  rushOrderAvailable: pricing.rushOrderAvailable,
  rushOrderMultiplier: pricing.rushOrderMultiplier?.toString() ?? '',
  bulkDiscountAvailable: pricing.bulkDiscountAvailable,
  acceptsCustomMaterials: pricing.acceptsCustomMaterials
})

export const FarmMarketplace: React.FC<FarmMarketplaceProps> = ({ activeTab, onUnreadCountChange, onOpenJob, pendingJobId, onPendingJobConsumed }) => {
  const MARKETPLACE_FEE_RATE = 0.1
  const [selectedJob, setSelectedJob] = useState<MarketplaceJob | null>(null)
  const [showBidForm, setShowBidForm] = useState(false)
  const [availableJobs, setAvailableJobs] = useState<MarketplaceJob[]>([])
  const [assignedJobs, setAssignedJobs] = useState<MarketplaceJob[]>([])
  const [bids, setBids] = useState<MarketplaceBid[]>([])
  const [quotes, setQuotes] = useState<MarketplaceQuote[]>([])
  const [, setQuotedJobIds] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem('marketplace-farm-quoted-jobs')
      if (!raw) return new Set()
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? new Set(parsed.filter((id): id is string => typeof id === 'string')) : new Set()
    } catch {
      return new Set()
    }
  })
  const [notifications, setNotifications] = useState<MarketplaceNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [farmProfile, setFarmProfile] = useState<FarmProfile | null>(null)
  const [farmPricing, setFarmPricing] = useState<FarmPricing | null>(null)
  const [profileForm, setProfileForm] = useState<FarmProfileFormState>(emptyProfileForm)
  const [availabilityForm, setAvailabilityForm] = useState<FarmAvailabilityFormState>(emptyAvailabilityForm)
  const [pricingForm, setPricingForm] = useState<FarmPricingFormState>(emptyPricingForm)
  const [verificationDocumentType, setVerificationDocumentType] = useState('tax_certificate')
  const [verificationNotes, setVerificationNotes] = useState('')
  const [verificationFile, setVerificationFile] = useState<File | null>(null)
  const [isLoadingJobs, setIsLoadingJobs] = useState(false)
  const [isLoadingAssignedJobs, setIsLoadingAssignedJobs] = useState(false)
  const [isLoadingBids, setIsLoadingBids] = useState(false)
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false)
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false)
  const [isLoadingSettings, setIsLoadingSettings] = useState(false)
  const [isSubmittingBid, setIsSubmittingBid] = useState(false)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isSavingAvailability, setIsSavingAvailability] = useState(false)
  const [isSavingPricing, setIsSavingPricing] = useState(false)
  const [isUploadingVerification, setIsUploadingVerification] = useState(false)
  const [updatingJobId, setUpdatingJobId] = useState<string | null>(null)
  const [withdrawingOfferId, setWithdrawingOfferId] = useState<string | null>(null)
  const [markingNotificationId, setMarkingNotificationId] = useState<string | null>(null)
  const [pageError, setPageError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [notificationError, setNotificationError] = useState<string | null>(null)
  const [workflowError, setWorkflowError] = useState<string | null>(null)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null)
  const [selectedAssignedJob, setSelectedAssignedJob] = useState<MarketplaceJob | null>(null)
  const minimumOrderNumeric = Number(pricingForm.minimumOrderValue)
  const estimatedFeeOnMinimumOrder = Number.isFinite(minimumOrderNumeric) ? minimumOrderNumeric * MARKETPLACE_FEE_RATE : null
  const estimatedPayoutOnMinimumOrder = Number.isFinite(minimumOrderNumeric) ? minimumOrderNumeric - (estimatedFeeOnMinimumOrder ?? 0) : null

  const applyFarmSettings = (profile: FarmProfile, pricing: FarmPricing) => {
    setFarmProfile(profile)
    setFarmPricing(pricing)
    setProfileForm(buildProfileFormState(profile))
    setAvailabilityForm(buildAvailabilityFormState(profile))
    setPricingForm(buildPricingFormState(pricing))
  }

  const hydrateJobsWithPendingOffers = (
    jobs: MarketplaceJob[],
    nextBids: MarketplaceBid[],
    nextQuotes: MarketplaceQuote[]
  ) => {
    const pendingBidJobIds = new Set(nextBids.filter(bid => bid.status === 'pending').map(bid => bid.jobId))
    const pendingQuoteJobIds = new Set(nextQuotes.filter(quote => quote.status === 'pending').map(quote => quote.jobId))

    return jobs.map(job => ({
      ...job,
      currentFarmHasBid: job.currentFarmHasBid === true || pendingBidJobIds.has(job.id),
      currentFarmHasQuote: pendingQuoteJobIds.has(job.id)
    }))
  }

  const loadAvailableJobs = async () => {
    setIsLoadingJobs(true)
    setPageError(null)

    try {
      const [nextJobs, nextBids, nextQuotes] = await Promise.all([
        backendService.listAvailableMarketplaceJobs(),
        backendService.listMyMarketplaceBids(),
        backendService.listMyMarketplaceQuotes()
      ])
      const hydratedJobs = hydrateJobsWithPendingOffers(nextJobs, nextBids, nextQuotes)
      setAvailableJobs(hydratedJobs)
      setBids(nextBids)
      setQuotes(nextQuotes)
      setSelectedJob(current => current ? hydratedJobs.find(job => job.id === current.id) || current : null)
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to load available jobs'))
    } finally {
      setIsLoadingJobs(false)
    }
  }

  const loadMyBids = async () => {
    setIsLoadingBids(true)
    setPageError(null)

    try {
      const [nextBids, nextQuotes] = await Promise.all([
        backendService.listMyMarketplaceBids(),
        backendService.listMyMarketplaceQuotes()
      ])
      setBids(nextBids)
      setQuotes(nextQuotes)
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to load your offers'))
    } finally {
      setIsLoadingBids(false)
    }
  }

  const loadAssignedJobs = async () => {
    setIsLoadingAssignedJobs(true)
    setPageError(null)

    try {
      const nextJobs = await backendService.listAssignedMarketplaceJobs()
      setAssignedJobs(nextJobs)
      setSelectedAssignedJob(current => (current ? nextJobs.find(job => job.id === current.id) || null : null))
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to load active jobs'))
    } finally {
      setIsLoadingAssignedJobs(false)
    }
  }

  const loadNotifications = async () => {
    setIsLoadingNotifications(true)
    setNotificationError(null)

    try {
      const response = await backendService.listMarketplaceNotifications()
      setNotifications(response.notifications)
      setUnreadCount(response.unreadCount)
    } catch (error) {
      setNotificationError(getErrorMessage(error, 'Failed to load marketplace inbox'))
    } finally {
      setIsLoadingNotifications(false)
    }
  }

  const loadFarmSettings = async () => {
    setIsLoadingSettings(true)
    setPageError(null)

    try {
      const [profile, pricing] = await Promise.all([
        backendService.getFarmProfile(),
        backendService.getFarmPricing()
      ])

      applyFarmSettings(profile, pricing)
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to load farm settings'))
    } finally {
      setIsLoadingSettings(false)
    }
  }

  const loadDashboard = async () => {
    setIsLoadingDashboard(true)
    setPageError(null)

    try {
      const [nextAvailableJobs, nextAssignedJobs, nextBids, nextQuotes, nextNotifications, profile, pricing] = await Promise.all([
        backendService.listAvailableMarketplaceJobs(),
        backendService.listAssignedMarketplaceJobs(),
        backendService.listMyMarketplaceBids(),
        backendService.listMyMarketplaceQuotes(),
        backendService.listMarketplaceNotifications(),
        backendService.getFarmProfile(),
        backendService.getFarmPricing()
      ])

      setAvailableJobs(hydrateJobsWithPendingOffers(nextAvailableJobs, nextBids, nextQuotes))
      setAssignedJobs(nextAssignedJobs)
      setBids(nextBids)
      setQuotes(nextQuotes)
      setNotifications(nextNotifications.notifications)
      setUnreadCount(nextNotifications.unreadCount)
      applyFarmSettings(profile, pricing)
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to load farm dashboard'))
    } finally {
      setIsLoadingDashboard(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'inbox') {
      void loadNotifications()
      return
    }

    if (activeTab === 'active-jobs') {
      void loadAssignedJobs()
      return
    }

    if (activeTab === 'my-bids') {
      void loadMyBids()
      return
    }

    if (activeTab === 'dashboard') {
      void loadDashboard()
      return
    }

    if (activeTab === 'settings') {
      void loadFarmSettings()
      return
    }

    if (activeTab === 'presets' || activeTab === 'girasoles-assistant') {
      return
    }

    void loadAvailableJobs()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  useEffect(() => {
    onUnreadCountChange?.(unreadCount)
  }, [unreadCount, onUnreadCountChange])

  useEffect(() => {
    if (!pendingJobId) return
    const match = availableJobs.find(job => job.id === pendingJobId)
      || assignedJobs.find(job => job.id === pendingJobId)
    if (match) {
      setSelectedJob(match)
      onPendingJobConsumed?.()
    }
  }, [pendingJobId, availableJobs, assignedJobs, onPendingJobConsumed])

  const handleMarkNotificationRead = async (notificationId: string) => {
    setMarkingNotificationId(notificationId)
    setNotificationError(null)

    try {
      const updatedNotification = await backendService.markMarketplaceNotificationRead(notificationId)
      setNotifications(current => current.map(notification => (
        notification.id === notificationId ? updatedNotification : notification
      )))
      setUnreadCount(current => Math.max(0, current - 1))
    } catch (error) {
      setNotificationError(getErrorMessage(error, 'Failed to mark notification as read'))
    } finally {
      setMarkingNotificationId(null)
    }
  }

  const handleSubmitBid = async (bidData: MarketplaceBidCreateRequest) => {
    if (!selectedJob) {
      return
    }

    setIsSubmittingBid(true)
    setFormError(null)

    try {
      await backendService.submitMarketplaceBid(selectedJob.id, bidData)
      await loadAvailableJobs()
      setShowBidForm(false)
      setSelectedJob(null)
    } catch (error) {
      setFormError(getErrorMessage(error, 'Failed to submit bid'))
    } finally {
      setIsSubmittingBid(false)
    }
  }

  const handleSubmitQuote = async (quoteData: MarketplaceQuoteCreateRequest) => {
    if (!selectedJob) {
      return
    }

    setIsSubmittingBid(true)
    setFormError(null)

    try {
      await backendService.submitMarketplaceQuote(selectedJob.id, quoteData)
      const jobIdForQuote = selectedJob.id
      setQuotedJobIds(prev => {
        const next = new Set(prev)
        next.add(jobIdForQuote)
        try { window.localStorage.setItem('marketplace-farm-quoted-jobs', JSON.stringify(Array.from(next))) } catch { /* ignore */ }
        return next
      })
      await loadAvailableJobs()
      setShowBidForm(false)
      setSelectedJob(null)
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to accept fixed price job')
      // Backend rejects duplicate quotes with this exact phrase; remember it so we hide the Accept button next time.
      if (message.toLowerCase().includes('already have a quote') && selectedJob) {
        const jobIdForQuote = selectedJob.id
        setQuotedJobIds(prev => {
          const next = new Set(prev)
          next.add(jobIdForQuote)
          try { window.localStorage.setItem('marketplace-farm-quoted-jobs', JSON.stringify(Array.from(next))) } catch { /* ignore */ }
          return next
        })
      }
      setFormError(message)
    } finally {
      setIsSubmittingBid(false)
    }
  }

  const handleWithdrawPendingBid = async (bid: MarketplaceBid) => {
    setWithdrawingOfferId(bid.id)
    setPageError(null)

    try {
      await backendService.withdrawMarketplaceBid(bid.id)
      await loadAvailableJobs()
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to cancel bid'))
    } finally {
      setWithdrawingOfferId(null)
    }
  }

  const handleWithdrawPendingQuote = async (quote: MarketplaceQuote) => {
    setWithdrawingOfferId(quote.id)
    setPageError(null)

    try {
      await backendService.withdrawMarketplaceQuote(quote.id)
      setQuotedJobIds(prev => {
        const next = new Set(prev)
        next.delete(quote.jobId)
        try { window.localStorage.setItem('marketplace-farm-quoted-jobs', JSON.stringify(Array.from(next))) } catch { /* ignore */ }
        return next
      })
      await loadAvailableJobs()
    } catch (error) {
      setPageError(getErrorMessage(error, 'Failed to cancel quote'))
    } finally {
      setWithdrawingOfferId(null)
    }
  }

  const handleAdvanceJobStatus = async (jobId: string, nextStatus: MarketplaceWorkflowStatus) => {
    setUpdatingJobId(jobId)
    setWorkflowError(null)

    try {
      const response = await backendService.updateMarketplaceJobStatus(jobId, nextStatus)
      const shouldRemoveFromActiveJobs = response.status === 'completed' || response.status === 'cancelled' || !response.job

      setAssignedJobs(current => {
        if (shouldRemoveFromActiveJobs) {
          return current.filter(job => job.id !== jobId)
        }

        return current.map(job => (job.id === jobId ? response.job! : job))
      })

      setSelectedAssignedJob(current => {
        if (!current || current.id !== jobId) {
          return current
        }

        return shouldRemoveFromActiveJobs ? null : response.job!
      })

      if (response.status === 'cancelled') {
        void loadAvailableJobs()
      }
    } catch (error) {
      setWorkflowError(getErrorMessage(error, 'Failed to update job status'))
    } finally {
      setUpdatingJobId(null)
    }
  }

  const handleSaveProfile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSavingProfile(true)
    setSettingsError(null)
    setSettingsSuccess(null)

    try {
      if (!profileForm.phone.trim()) {
        throw new Error('Phone is required')
      }

      const maxBuildVolumeValues = [profileForm.maxBuildVolumeX, profileForm.maxBuildVolumeY, profileForm.maxBuildVolumeZ]
      const hasBuildVolume = maxBuildVolumeValues.some(value => value.trim() !== '')

      if (hasBuildVolume && maxBuildVolumeValues.some(value => value.trim() === '')) {
        throw new Error('Provide all max build volume dimensions or leave them all blank')
      }

      const updatedProfile = await backendService.updateFarmProfile({
        phone: profileForm.phone || undefined,
        description: profileForm.description || undefined,
        logoUrl: profileForm.logoUrl || undefined,
        address: {
          line1: profileForm.addressLine1 || undefined,
          line2: profileForm.addressLine2 || undefined,
          city: profileForm.city || undefined,
          stateProvince: profileForm.stateProvince || undefined,
          postalCode: profileForm.postalCode || undefined,
          country: profileForm.country || undefined
        },
        serviceRadiusKm: parseOptionalNumber(profileForm.serviceRadiusKm, 'Service radius'),
        averageTurnaroundDays: parseOptionalNumber(profileForm.averageTurnaroundDays, 'Average turnaround'),
        shipsNationally: profileForm.shipsNationally,
        shipsInternationally: profileForm.shipsInternationally,
        printerCount: parseOptionalNumber(profileForm.printerCount, 'Printer count'),
        printerModels: parseStringList(profileForm.printerModels),
        materialsAvailable: parseStringList(profileForm.materialsAvailable),
        maxBuildVolume: hasBuildVolume
          ? {
              x: parseOptionalNumber(profileForm.maxBuildVolumeX, 'Build volume X') ?? 0,
              y: parseOptionalNumber(profileForm.maxBuildVolumeY, 'Build volume Y') ?? 0,
              z: parseOptionalNumber(profileForm.maxBuildVolumeZ, 'Build volume Z') ?? 0
            }
          : undefined,
        certifications: parseStringList(profileForm.certifications)
      })

      setFarmProfile(updatedProfile)
      setFarmPricing(updatedProfile.pricing)
      setProfileForm(buildProfileFormState(updatedProfile))
      setAvailabilityForm(buildAvailabilityFormState(updatedProfile))
      setSettingsSuccess('Farm profile saved successfully')
    } catch (error) {
      setSettingsError(getErrorMessage(error, 'Failed to save farm profile'))
    } finally {
      setIsSavingProfile(false)
    }
  }

  const handleSaveAvailability = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSavingAvailability(true)
    setSettingsError(null)
    setSettingsSuccess(null)

    try {
      const updatedAvailability = await backendService.updateFarmAvailability({
        operatingHours: availabilityForm.schedule,
        vacationMode: availabilityForm.vacationMode,
        vacationStart: availabilityForm.vacationStart || undefined,
        vacationEnd: availabilityForm.vacationEnd || undefined,
        vacationMessage: availabilityForm.vacationMessage || undefined
      })

      setFarmProfile(current => (current ? {
        ...current,
        availability: updatedAvailability
      } : current))
      setAvailabilityForm({
        schedule: parseSchedule(updatedAvailability.operatingHours),
        vacationMode: updatedAvailability.vacationMode,
        vacationStart: formatDateInput(updatedAvailability.vacationStart),
        vacationEnd: formatDateInput(updatedAvailability.vacationEnd),
        vacationMessage: updatedAvailability.vacationMessage ?? ''
      })
      setSettingsSuccess('Availability saved successfully')
    } catch (error) {
      setSettingsError(getErrorMessage(error, 'Failed to save farm availability'))
    } finally {
      setIsSavingAvailability(false)
    }
  }

  const handleSavePricing = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSavingPricing(true)
    setSettingsError(null)
    setSettingsSuccess(null)

    try {
      const updatedPricing = await backendService.updateFarmPricing({
        minimumOrderValue: parseOptionalNumber(pricingForm.minimumOrderValue, 'Minimum order value'),
        rushOrderAvailable: pricingForm.rushOrderAvailable,
        rushOrderMultiplier: parseOptionalNumber(pricingForm.rushOrderMultiplier, 'Rush order multiplier'),
        bulkDiscountAvailable: pricingForm.bulkDiscountAvailable,
        acceptsCustomMaterials: pricingForm.acceptsCustomMaterials
      })

      setFarmPricing(updatedPricing)
      setPricingForm(buildPricingFormState(updatedPricing))
      setFarmProfile(current => (current ? {
        ...current,
        pricing: updatedPricing
      } : current))
      setSettingsSuccess('Pricing saved successfully')
    } catch (error) {
      setSettingsError(getErrorMessage(error, 'Failed to save farm pricing'))
    } finally {
      setIsSavingPricing(false)
    }
  }

  const handleUploadVerification = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsUploadingVerification(true)
    setSettingsError(null)
    setSettingsSuccess(null)

    try {
      if (!verificationFile) {
        throw new Error('Select a verification document to upload')
      }

      const verification = await backendService.uploadFarmVerificationDocument(
        verificationFile,
        verificationDocumentType,
        verificationNotes || undefined
      )

      setFarmProfile(current => (current ? {
        ...current,
        verification
      } : current))
      setVerificationNotes('')
      setVerificationFile(null)
      setSettingsSuccess('Verification document uploaded successfully')
    } catch (error) {
      setSettingsError(getErrorMessage(error, 'Failed to upload verification document'))
    } finally {
      setIsUploadingVerification(false)
    }
  }

  const pendingBidsCount = bids.filter(bid => bid.status === 'pending').length
  const pendingBidJobIds = new Set(bids.filter(bid => bid.status === 'pending').map(bid => bid.jobId))
  const pendingQuoteJobIds = new Set(quotes.filter(quote => quote.status === 'pending').map(quote => quote.jobId))
  const selectedPendingBid = selectedJob
    ? bids.find(bid => bid.jobId === selectedJob.id && bid.status === 'pending')
    : undefined
  const selectedPendingQuote = selectedJob
    ? quotes.find(quote => quote.jobId === selectedJob.id && quote.status === 'pending')
    : undefined
  const verificationStatus = formatVerificationStatus(farmProfile?.verification.status)
  const isVerified = farmProfile ? farmProfile.verification.status === 'verified' : true
  const verificationDocuments = farmProfile?.verification.documents ?? []
  const capabilityTags = [
    `${farmProfile?.capabilities.printerCount ?? 0} printers`,
    ...(farmProfile?.capabilities.materialsAvailable ?? []),
    ...(farmProfile?.capabilities.certifications ?? [])
  ]

  if (activeTab === 'dashboard') {
    return (
      <div className="farm-marketplace">
        <div className="farm-header">
          <h2>Farm Dashboard</h2>
        </div>

        {pageError && <div className="marketplace-error-banner">{pageError}</div>}

        {isLoadingDashboard ? (
          <div className="placeholder">
            <p>Loading farm dashboard...</p>
          </div>
        ) : (
          <div className="farm-dashboard-layout">
            <section className="farm-kpi-grid" aria-label="Farm dashboard metrics">
              <article className="farm-kpi-card">
                <span className="farm-kpi-label">Active Jobs</span>
                <strong className="farm-kpi-value">{assignedJobs.length}</strong>
              </article>
              <article className="farm-kpi-card">
                <span className="farm-kpi-label">Open Jobs to Bid</span>
                <strong className="farm-kpi-value">{availableJobs.length}</strong>
              </article>
              <article className="farm-kpi-card">
                <span className="farm-kpi-label">Pending Bids</span>
                <strong className="farm-kpi-value">{pendingBidsCount}</strong>
              </article>
              <article className="farm-kpi-card">
                <span className="farm-kpi-label">Unread Messages</span>
                <strong className="farm-kpi-value">{unreadCount}</strong>
              </article>
              <article className="farm-kpi-card">
                <span className="farm-kpi-label">Verification Status</span>
                <strong className="farm-kpi-value">{verificationStatus}</strong>
              </article>
              <article className="farm-kpi-card">
                <span className="farm-kpi-label">Rush Orders</span>
                <strong className="farm-kpi-value">{farmPricing?.rushOrderAvailable ? 'Enabled' : 'Disabled'}</strong>
              </article>
            </section>

            <section className="farm-summary-grid">
              <article className="farm-panel">
                <h3>Business Snapshot</h3>
                <dl className="farm-detail-list">
                  <div>
                    <dt>Business</dt>
                    <dd>{farmProfile?.businessName || 'Not configured'}</dd>
                  </div>
                  <div>
                    <dt>Owner</dt>
                    <dd>{farmProfile?.ownerName || 'Not available'}</dd>
                  </div>
                  <div>
                    <dt>Location</dt>
                    <dd>{[farmProfile?.address.city, farmProfile?.address.country].filter(Boolean).join(', ') || 'Not configured'}</dd>
                  </div>
                  <div>
                    <dt>Service Radius</dt>
                    <dd>{farmProfile?.serviceRadiusKm ? `${farmProfile.serviceRadiusKm} km` : 'Not configured'}</dd>
                  </div>
                </dl>
              </article>

              <article className="farm-panel">
                <h3>Capabilities</h3>
                <ul className="farm-tag-list">
                  {capabilityTags.map(tag => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              </article>

              <article className="farm-panel">
                <h3>Pricing Overview</h3>
                <dl className="farm-detail-list">
                  <div>
                    <dt>Minimum Order</dt>
                    <dd>{farmPricing ? `$${farmPricing.minimumOrderValue.toFixed(2)}` : 'Not configured'}</dd>
                  </div>
                  <div>
                    <dt>Rush Multiplier</dt>
                    <dd>{farmPricing?.rushOrderMultiplier ? `${farmPricing.rushOrderMultiplier}x` : 'N/A'}</dd>
                  </div>
                  <div>
                    <dt>Bulk Discounts</dt>
                    <dd>{farmPricing?.bulkDiscountAvailable ? 'Available' : 'Not offered'}</dd>
                  </div>
                  <div>
                    <dt>Custom Materials</dt>
                    <dd>{farmPricing?.acceptsCustomMaterials ? 'Accepted' : 'Not accepted'}</dd>
                  </div>
                </dl>
              </article>

              <article className="farm-panel">
                <h3>Verification Documents</h3>
                {verificationDocuments.length ? (
                  <ul className="farm-document-list">
                    {verificationDocuments.map(document => (
                      <li key={document.id}>
                        <span>{document.originalName}</span>
                        <span>{formatFileSize(document.size)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="farm-muted">No verification documents uploaded yet.</p>
                )}
              </article>
            </section>
          </div>
        )}
      </div>
    )
  }

  if (activeTab === 'settings') {
    return (
      <div className="farm-marketplace">
        <div className="farm-header">
          <h2>Farm Settings</h2>
        </div>

        {pageError && <div className="marketplace-error-banner">{pageError}</div>}
        {settingsError && <div className="marketplace-error-banner">{settingsError}</div>}
        {settingsSuccess && <div className="marketplace-success-banner">{settingsSuccess}</div>}

        {isLoadingSettings ? (
          <div className="placeholder">
            <p>Loading farm settings...</p>
          </div>
        ) : (
          <div className="farm-settings-layout">
            <form className="farm-panel farm-form" onSubmit={handleSaveProfile}>
              <div className="farm-section-header">
                <h3>Business Profile</h3>
                <button type="submit" className="farm-primary-button" disabled={isSavingProfile}>
                  {isSavingProfile ? 'Saving...' : 'Save Business Profile'}
                </button>
              </div>

              <div className="farm-form-grid">
                <label className="farm-readonly-field">
                  Farm Name (system assigned)
                  <input value={farmProfile?.businessName ?? farmProfile?.username ?? ''} readOnly disabled aria-readonly="true" />
                </label>
                <label>
                  Phone
                  <input value={profileForm.phone} onChange={event => setProfileForm(current => ({ ...current, phone: event.target.value }))} required />
                </label>
                <label className="farm-field-span-2">
                  Description
                  <textarea value={profileForm.description} onChange={event => setProfileForm(current => ({ ...current, description: event.target.value }))} rows={3} />
                </label>
                <label className="farm-field-span-2">
                  Logo URL
                  <input value={profileForm.logoUrl} onChange={event => setProfileForm(current => ({ ...current, logoUrl: event.target.value }))} />
                </label>
                <label>
                  Address Line 1
                  <input value={profileForm.addressLine1} onChange={event => setProfileForm(current => ({ ...current, addressLine1: event.target.value }))} />
                </label>
                <label>
                  Address Line 2
                  <input value={profileForm.addressLine2} onChange={event => setProfileForm(current => ({ ...current, addressLine2: event.target.value }))} />
                </label>
                <label>
                  City
                  <input value={profileForm.city} onChange={event => setProfileForm(current => ({ ...current, city: event.target.value }))} />
                </label>
                <label>
                  State / Province
                  <input value={profileForm.stateProvince} onChange={event => setProfileForm(current => ({ ...current, stateProvince: event.target.value }))} />
                </label>
                <label>
                  Postal Code
                  <input value={profileForm.postalCode} onChange={event => setProfileForm(current => ({ ...current, postalCode: event.target.value }))} />
                </label>
                <label>
                  Country
                  <input value={profileForm.country} onChange={event => setProfileForm(current => ({ ...current, country: event.target.value }))} />
                </label>
                <label>
                  Service Radius (km)
                  <input value={profileForm.serviceRadiusKm} onChange={event => setProfileForm(current => ({ ...current, serviceRadiusKm: event.target.value }))} />
                </label>
                <label>
                  Average Turnaround (days)
                  <input value={profileForm.averageTurnaroundDays} onChange={event => setProfileForm(current => ({ ...current, averageTurnaroundDays: event.target.value }))} />
                </label>
                <label>
                  Printer Count
                  <input value={profileForm.printerCount} onChange={event => setProfileForm(current => ({ ...current, printerCount: event.target.value }))} />
                </label>
                <label>
                  Printer Models (comma separated)
                  <input value={profileForm.printerModels} onChange={event => setProfileForm(current => ({ ...current, printerModels: event.target.value }))} />
                </label>
                <label>
                  Materials Available (comma separated)
                  <input value={profileForm.materialsAvailable} onChange={event => setProfileForm(current => ({ ...current, materialsAvailable: event.target.value }))} />
                </label>
                <label>
                  Max Build Volume X
                  <input value={profileForm.maxBuildVolumeX} onChange={event => setProfileForm(current => ({ ...current, maxBuildVolumeX: event.target.value }))} />
                </label>
                <label>
                  Max Build Volume Y
                  <input value={profileForm.maxBuildVolumeY} onChange={event => setProfileForm(current => ({ ...current, maxBuildVolumeY: event.target.value }))} />
                </label>
                <label>
                  Max Build Volume Z
                  <input value={profileForm.maxBuildVolumeZ} onChange={event => setProfileForm(current => ({ ...current, maxBuildVolumeZ: event.target.value }))} />
                </label>
                <label className="farm-field-span-2">
                  Certifications (comma separated)
                  <input value={profileForm.certifications} onChange={event => setProfileForm(current => ({ ...current, certifications: event.target.value }))} />
                </label>
              </div>

              <fieldset className="farm-shipping-fieldset">
                <legend>Shipping</legend>
                <label><input type="checkbox" checked={profileForm.shipsNationally} onChange={event => setProfileForm(current => ({ ...current, shipsNationally: event.target.checked }))} /> Ships Nationally</label>
                <label><input type="checkbox" checked={profileForm.shipsInternationally} onChange={event => setProfileForm(current => ({ ...current, shipsInternationally: event.target.checked }))} /> Ships Internationally</label>
              </fieldset>
            </form>

            <form className="farm-panel farm-form" onSubmit={handleSaveAvailability}>
              <div className="farm-section-header">
                <h3>Availability</h3>
                <button type="submit" className="farm-primary-button" disabled={isSavingAvailability}>
                  {isSavingAvailability ? 'Saving...' : 'Save Availability'}
                </button>
              </div>

              <div className="farm-field-span-2">
                <span className="farm-schedule-label">Operating Hours</span>
                <div className="farm-schedule-grid">
                  {DAYS.map(day => {
                    const ds = availabilityForm.schedule[day]
                    const setDay = (patch: Partial<DaySchedule>) =>
                      setAvailabilityForm(cur => ({
                        ...cur,
                        schedule: { ...cur.schedule, [day]: { ...cur.schedule[day], ...patch } }
                      }))
                    return (
                      <div key={day} className={`farm-schedule-row${ds.enabled ? '' : ' farm-schedule-row--closed'}`}>
                        <label className="farm-schedule-day">
                          <input type="checkbox" checked={ds.enabled} onChange={e => setDay({ enabled: e.target.checked })} />
                          {DAY_LABELS[day]}
                        </label>
                        {ds.enabled ? (
                          <>
                            <div className="farm-schedule-times">
                              <input type="time" value={ds.open}  onChange={e => setDay({ open:  e.target.value })} />
                              <span className="farm-schedule-sep">—</span>
                              <input type="time" value={ds.close} onChange={e => setDay({ close: e.target.value })} />
                            </div>
                            <div className="farm-schedule-delivery">
                              <label className="farm-schedule-delivers">
                                <input type="checkbox" checked={ds.delivers} onChange={e => setDay({ delivers: e.target.checked })} />
                                Delivers
                              </label>
                              {ds.delivers && (
                                <div className="farm-schedule-deliver-until">
                                  <span className="farm-muted">until</span>
                                  <input type="time" value={ds.deliverUntil} onChange={e => setDay({ deliverUntil: e.target.value })} />
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="farm-muted farm-schedule-closed-label">Closed</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="farm-form-grid">
                <label className="farm-checkbox-inline">
                  <input type="checkbox" checked={availabilityForm.vacationMode} onChange={event => setAvailabilityForm(current => ({ ...current, vacationMode: event.target.checked }))} />
                  Vacation Mode
                </label>
                <label>
                  Vacation Start
                  <input type="date" value={availabilityForm.vacationStart} onChange={event => setAvailabilityForm(current => ({ ...current, vacationStart: event.target.value }))} />
                </label>
                <label>
                  Vacation End
                  <input type="date" value={availabilityForm.vacationEnd} onChange={event => setAvailabilityForm(current => ({ ...current, vacationEnd: event.target.value }))} />
                </label>
                <label className="farm-field-span-2">
                  Vacation Message
                  <textarea value={availabilityForm.vacationMessage} onChange={event => setAvailabilityForm(current => ({ ...current, vacationMessage: event.target.value }))} rows={3} />
                </label>
              </div>
            </form>

            <form className="farm-panel farm-form" onSubmit={handleSavePricing}>
              <div className="farm-section-header">
                <h3>Pricing</h3>
                <button type="submit" className="farm-primary-button" disabled={isSavingPricing}>
                  {isSavingPricing ? 'Saving...' : 'Save Pricing'}
                </button>
              </div>

              <div className="farm-form-grid">
                <label>
                  Minimum Order Value
                  <input value={pricingForm.minimumOrderValue} onChange={event => setPricingForm(current => ({ ...current, minimumOrderValue: event.target.value }))} />
                </label>
                <label>
                  Rush Order Multiplier
                  <input value={pricingForm.rushOrderMultiplier} onChange={event => setPricingForm(current => ({ ...current, rushOrderMultiplier: event.target.value }))} />
                </label>
              </div>

              {estimatedPayoutOnMinimumOrder !== null && estimatedFeeOnMinimumOrder !== null && (
                <p className="farm-muted">
                  Estimated payout on minimum order after {(MARKETPLACE_FEE_RATE * 100).toFixed(0)}% fee: ${estimatedPayoutOnMinimumOrder.toFixed(2)} (fee: ${estimatedFeeOnMinimumOrder.toFixed(2)})
                </p>
              )}

              <div className="farm-toggle-grid">
                <label><input type="checkbox" checked={pricingForm.rushOrderAvailable} onChange={event => setPricingForm(current => ({ ...current, rushOrderAvailable: event.target.checked }))} /> Rush Orders Available</label>
                <label><input type="checkbox" checked={pricingForm.bulkDiscountAvailable} onChange={event => setPricingForm(current => ({ ...current, bulkDiscountAvailable: event.target.checked }))} /> Bulk Discount Available</label>
                <label><input type="checkbox" checked={pricingForm.acceptsCustomMaterials} onChange={event => setPricingForm(current => ({ ...current, acceptsCustomMaterials: event.target.checked }))} /> Accepts Custom Materials</label>
              </div>
            </form>

            <form className="farm-panel farm-form" onSubmit={handleUploadVerification}>
              <div className="farm-section-header">
                <h3>Verification</h3>
                <span className="farm-status-pill">{verificationStatus}</span>
              </div>

              <div className="farm-form-grid">
                <label>
                  Document Type
                  <input value={verificationDocumentType} onChange={event => setVerificationDocumentType(event.target.value)} />
                </label>
                <label>
                  Verification Document
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={event => setVerificationFile(event.target.files?.[0] || null)}
                  />
                </label>
                <label className="farm-field-span-2">
                  Notes
                  <textarea value={verificationNotes} onChange={event => setVerificationNotes(event.target.value)} rows={3} />
                </label>
              </div>

              <button type="submit" className="farm-primary-button" disabled={isUploadingVerification}>
                {isUploadingVerification ? 'Uploading...' : 'Upload Document'}
              </button>

              {verificationDocuments.length ? (
                <ul className="farm-document-list farm-document-list-detailed">
                  {verificationDocuments.map(document => (
                    <li key={document.id}>
                      <div>
                        <strong>{document.originalName}</strong>
                        <p>{document.documentType}</p>
                      </div>
                      <span>{formatFileSize(document.size)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="farm-muted">No verification documents uploaded yet.</p>
              )}
            </form>
          </div>
        )}
      </div>
    )
  }

  if (activeTab === 'inbox') {
    return (
      <div className="farm-marketplace">
        <MarketplaceInbox
          title="Inbox"
          notifications={notifications}
          unreadCount={unreadCount}
          isLoading={isLoadingNotifications}
          errorMessage={notificationError}
          markingNotificationId={markingNotificationId}
          onRefresh={loadNotifications}
          onMarkAsRead={handleMarkNotificationRead}
          onOpenJob={onOpenJob}
        />
      </div>
    )
  }

  if (activeTab === 'my-bids') {
    return (
      <div className="farm-marketplace">
        <div className="farm-header">
          <h2>My Bids</h2>
        </div>

        {pageError && <div className="marketplace-error-banner">{pageError}</div>}

        {isLoadingBids ? (
          <div className="placeholder">
            <p>Loading your bids...</p>
          </div>
        ) : (
          <FarmBidsList
            bids={bids}
            isWithdrawingBid={Boolean(withdrawingOfferId)}
            onWithdrawBid={handleWithdrawPendingBid}
          />
        )}
      </div>
    )
  }

  if (activeTab === 'active-jobs') {
    return (
      <div className="farm-marketplace">
        <div className="farm-header">
          <h2>Active Jobs</h2>
        </div>

        {pageError && !selectedAssignedJob && <div className="marketplace-error-banner">{pageError}</div>}
        {workflowError && <div className="marketplace-error-banner">{workflowError}</div>}

        {isLoadingAssignedJobs ? (
          <div className="placeholder">
            <p>Loading active marketplace jobs...</p>
          </div>
        ) : (
          <FarmActiveJobsList
            jobs={assignedJobs}
            selectedJob={selectedAssignedJob}
            updatingJobId={updatingJobId}
            onSelectJob={setSelectedAssignedJob}
            onBack={() => setSelectedAssignedJob(null)}
            onAdvanceStatus={handleAdvanceJobStatus}
          />
        )}
      </div>
    )
  }

  if (activeTab === 'girasoles-assistant') {
    return <FarmAIChat />
  }

  if (activeTab === 'presets') {
    return (
      <div className="farm-marketplace">
        <PresetsBrowser />
      </div>
    )
  }

  return (
    <div className="farm-marketplace">
      <div className="farm-header">
        <h2>Available Jobs</h2>
      </div>

      {pageError && !showBidForm && <div className="marketplace-error-banner">{pageError}</div>}

      {!isVerified && farmProfile && (
        <div className="marketplace-warning-banner">
          ⚠️ Your farm account is pending validation. You cannot submit bids or accept jobs until approved by an admin.
        </div>
      )}

      {selectedJob && !showBidForm ? (
        <JobDetailsForFarm
          job={selectedJob}
          onBack={() => setSelectedJob(null)}
          onSubmitBid={() => setShowBidForm(true)}
          onUpdateJob={setSelectedJob}
          isVerified={isVerified}
          alreadyBid={selectedJob.currentFarmHasBid === true || Boolean(selectedPendingBid)}
          alreadyQuoted={Boolean(selectedPendingQuote)}
          pendingBid={selectedPendingBid}
          pendingQuote={selectedPendingQuote}
          isWithdrawingOffer={Boolean(withdrawingOfferId && (withdrawingOfferId === selectedPendingBid?.id || withdrawingOfferId === selectedPendingQuote?.id))}
          onCancelPendingBid={handleWithdrawPendingBid}
          onCancelPendingQuote={handleWithdrawPendingQuote}
        />
      ) : selectedJob && showBidForm && selectedJob.pricingModel === 'fixed_price' ? (
        <QuoteAcceptForm
          job={selectedJob}
          isSubmitting={isSubmittingBid}
          errorMessage={formError}
          onAccept={async (data) => {
            await handleSubmitQuote({
              quoteAmount: selectedJob.fixedPrice ?? 0,
              estimatedDays: data.estimatedDays,
              notes: data.notes
            })
          }}
          onCancel={() => setShowBidForm(false)}
        />
      ) : selectedJob && showBidForm ? (
        <BidSubmissionForm
          job={selectedJob}
          onSubmit={handleSubmitBid}
          isSubmitting={isSubmittingBid}
          errorMessage={formError}
          onCancel={() => setShowBidForm(false)}
        />
      ) : isLoadingJobs ? (
        <div className="placeholder">
          <p>Loading available marketplace jobs...</p>
        </div>
      ) : (
        <AvailableJobsList
          jobs={availableJobs}
          onSelectJob={setSelectedJob}
          bidJobIds={pendingBidJobIds}
          quoteJobIds={pendingQuoteJobIds}
        />
      )}
    </div>
  )
}
